import { useMemo } from 'react';
import _ from 'lodash';
import { buildDisplayLogic } from '../../../tester/pages/survey/helpers';

/**
 * Custom hook to identify which survey questions have been skipped based on display question logic.
 *
 * @param {Array} surveyPages - An array of survey page objects.
 * @param {Array} answers - An array of answer objects provided by the participant.
 * @param {Array} skippedPages - An array of skipped pages ids.
 * @returns {Array} skippedQuestions - An array of skipped questions.
 */
const useIdentifySkippedQuestions = (surveyPages, answers, skippedPages) => useMemo(() => {
  const skippedQuestions = [];
  const orderedSurveyPages = _.sortBy(surveyPages, 'order');
  const skippedPageIds = new Set(skippedPages.map((page) => page.pageId));

  orderedSurveyPages.forEach((page) => {
    if (skippedPageIds.has(page.id)) return;

    // Apply buildDisplayLogic to determine visibility of questions
    const displayLogic = buildDisplayLogic(answers, orderedSurveyPages);

    page.questions.forEach((question) => {
      const isVisible = displayLogic[question.id] !== false; // Default to true if undefined

      const isAnswered = answers.some(
        (answer) => answer.questionId === question.id && answer.questionOptionId,
      );

      if (!isVisible && !isAnswered) {
        skippedQuestions.push({
          pageId: page.id,
          pageOrder: page.order,
          questionId: question.id,
          questionLabel: question.label,
          questionOrder: question.questionOrder,
          questionType: question.questionType,
          skippedQuestionMessage: 'This question was skipped due to display logic rules.',
        });
      }
    });
  });

  return skippedQuestions;
}, [surveyPages, answers, skippedPages]);

export default useIdentifySkippedQuestions;
